<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Copy, ExternalLink, ChevronDown, Sparkles } from "lucide-vue-next";
import { api } from "@/api/client";
import type { Playground, PlaygroundStatus } from "@/api/types";
import { authStore } from "@/auth/authStore";
import { pushToast } from "@/composables/useToasts";
import { copyText } from "@/composables/useClipboard";
import { formatAge } from "@/composables/useTime";
import StatusPill from "@/components/StatusPill.vue";
import Table, { type Column } from "@/components/Table.vue";

const router = useRouter();
const route = useRoute();

const loading = ref(true);
const error = ref<string | null>(null);
const items = ref<Playground[]>([]);

const search = ref<string>((route.query.q as string) ?? "");
const statusFilter = ref<PlaygroundStatus | "any">(
  (route.query.status as PlaygroundStatus) ?? "any",
);
const ownerFilter = ref<"anyone" | "self" | string>(
  (route.query.owner as string) ?? "anyone",
);

const sortKey = ref<string | null>((route.query.sort as string) ?? "createdAt");
const sortDir = ref<"asc" | "desc">((route.query.dir as "asc" | "desc") ?? "desc");

const searchInput = ref<HTMLInputElement | null>(null);

watch([search, statusFilter, ownerFilter, sortKey, sortDir], () => {
  router.replace({
    query: {
      ...route.query,
      q: search.value || undefined,
      status: statusFilter.value !== "any" ? statusFilter.value : undefined,
      owner: ownerFilter.value !== "anyone" ? ownerFilter.value : undefined,
      sort: sortKey.value ?? undefined,
      dir: sortDir.value,
    },
  });
});

async function load() {
  loading.value = true;
  error.value = null;
  try {
    items.value = await api.listPlaygrounds();
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Failed to load playgrounds.";
  } finally {
    loading.value = false;
  }
}

const owners = computed(() => {
  const set = new Set<string>();
  for (const p of items.value) set.add(p.createdBy.upn);
  return [...set];
});

// True when we've finished loading and there are zero playgrounds AT ALL
// (regardless of filters). Triggers the prominent "first run" empty state
// in place of the filter bar + table. When there ARE playgrounds but the
// filter excludes them, the table's smaller "No matches" stays.
const showFirstRunEmpty = computed(
  () => !loading.value && !error.value && items.value.length === 0,
);

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  return items.value.filter((p) => {
    if (statusFilter.value !== "any" && p.status !== statusFilter.value) return false;
    if (ownerFilter.value === "self") {
      if (p.createdBy.upn !== authStore.state.me?.upn) return false;
    } else if (ownerFilter.value !== "anyone") {
      if (p.createdBy.upn !== ownerFilter.value) return false;
    }
    if (q && !p.customer.toLowerCase().includes(q) && !p.createdBy.upn.toLowerCase().includes(q)) {
      return false;
    }
    return true;
  });
});

const columns: Column<Playground>[] = [
  {
    key: "status",
    header: "Status",
    sortable: true,
    width: "140px",
    sortValue: (r) => r.status,
  },
  {
    key: "customer",
    header: "Customer",
    sortable: true,
    sortValue: (r) => r.customer,
  },
  {
    key: "owner",
    header: "Owner",
    sortable: true,
    sortValue: (r) => r.createdBy.upn,
  },
  {
    key: "age",
    header: "Age",
    sortable: true,
    width: "80px",
    sortValue: (r) => new Date(r.createdAt).getTime(),
  },
  {
    key: "url",
    header: "URL",
    sortable: false,
  },
  {
    key: "actions",
    header: "",
    width: "44px",
  },
];

function onSort(key: string) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortDir.value = "asc";
  }
}

function rowKey(p: Playground) {
  return p.customer;
}

function open(p: Playground) {
  router.push(`/playgrounds/${p.customer}`);
}

async function copyUrl(p: Playground) {
  try {
    await copyText(`https://${p.fqdn}`);
    pushToast("URL copied.", "info");
  } catch {
    pushToast("Could not copy.", "error");
  }
}

const openMenuFor = ref<string | null>(null);
/**
 * Pixel position to render the row-actions menu at, in viewport
 * coordinates. We render the menu via <Teleport to="body"> so it
 * isn't clipped by Table.vue's `overflow-x-auto` wrapper (which
 * creates a scroll containing block that swallows absolute-positioned
 * children). `top` is the bottom of the trigger button + 4px gap;
 * `right` is distance from the viewport's right edge so the menu's
 * right edge aligns with the chevron button.
 */
const menuPos = ref<{ top: number; right: number } | null>(null);

function toggleMenu(customer: string, ev: MouseEvent): void {
  if (openMenuFor.value === customer) {
    openMenuFor.value = null;
    menuPos.value = null;
    return;
  }
  const btn = ev.currentTarget as HTMLElement;
  const rect = btn.getBoundingClientRect();
  menuPos.value = {
    top: rect.bottom + 4,
    right: window.innerWidth - rect.right,
  };
  openMenuFor.value = customer;
}

function closeMenu(): void {
  openMenuFor.value = null;
  menuPos.value = null;
}

// Close the menu on outside click / Escape / window resize. Bound for
// the lifetime of the page; cheap enough not to gate on menu-open.
function onDocClick(ev: MouseEvent): void {
  if (!openMenuFor.value) return;
  const t = ev.target as HTMLElement;
  // Clicks inside either the trigger button or the teleported menu
  // are handled by their own handlers (which call closeMenu).
  if (t.closest("[data-row-menu]") || t.closest("[data-row-menu-trigger]")) {
    return;
  }
  closeMenu();
}
function onEsc(ev: KeyboardEvent): void {
  if (ev.key === "Escape") closeMenu();
}
function onResize(): void {
  // Position is captured from the trigger's rect at open time — if the
  // viewport scrolls or resizes we'd be left pointing at empty space.
  // Cheapest correction is just to close; opening again is one click.
  closeMenu();
}

function canDelete(p: Playground) {
  return (
    authStore.isAdmin.value ||
    p.createdBy.upn === authStore.state.me?.upn
  );
}

// Keyboard shortcuts: `/` focus search, `n` create.
function onGlobalKey(e: KeyboardEvent) {
  const target = e.target as HTMLElement | null;
  const inField =
    target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
  if (inField) return;
  if (e.key === "/") {
    e.preventDefault();
    searchInput.value?.focus();
  } else if (e.key === "n" || e.key === "N") {
    e.preventDefault();
    router.push("/playgrounds/new");
  }
}

onMounted(() => {
  document.addEventListener("keydown", onGlobalKey);
  document.addEventListener("click", onDocClick, true);
  document.addEventListener("keydown", onEsc);
  window.addEventListener("resize", onResize);
  window.addEventListener("scroll", onResize, true);
  void load();
});
onUnmounted(() => {
  document.removeEventListener("keydown", onGlobalKey);
  document.removeEventListener("click", onDocClick, true);
  document.removeEventListener("keydown", onEsc);
  window.removeEventListener("resize", onResize);
  window.removeEventListener("scroll", onResize, true);
});
</script>

<template>
  <section class="mx-auto max-w-7xl px-6 py-6">
    <header class="mb-4 flex items-center justify-between gap-3">
      <h1 class="text-base font-medium">Playgrounds</h1>
      <router-link
        to="/playgrounds/new"
        class="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 dark:bg-accent-dark"
      >+ New playground</router-link>
    </header>

    <div v-if="error" class="mb-3 rounded border border-status-failed/40 bg-status-failed/10 p-3 text-sm text-status-failed">
      {{ error }}
      <button class="ml-2 underline" @click="load">retry</button>
    </div>

    <!-- First-run empty state: prominent CTA, no filter bar (nothing to filter) -->
    <div
      v-if="showFirstRunEmpty"
      class="mx-auto mt-12 max-w-md rounded-lg border border-border bg-surface px-6 py-10 text-center dark:border-border-dark dark:bg-surface-dark"
    >
      <Sparkles :size="28" class="mx-auto text-fg-muted" />
      <h2 class="mt-3 text-sm font-medium">No playgrounds yet</h2>
      <p class="mt-2 text-xs text-fg-muted">
        Spin up an isolated Operate environment on Azure to demo, prototype, or experiment.
        Each deploy takes 10–15 minutes and tears down cleanly when you're done.
      </p>
      <router-link
        to="/playgrounds/new"
        class="mt-5 inline-flex items-center gap-2 rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 dark:bg-accent-dark"
      >+ Create your first playground</router-link>
    </div>

    <!-- Normal view: filters + table. Suppressed during first-run-empty so the
         empty state above isn't competing with empty filter controls. -->
    <template v-if="!showFirstRunEmpty">
      <div class="mb-3 flex flex-wrap items-center gap-3 text-sm">
        <label class="flex items-center gap-2">
          <span class="text-2xs text-fg-muted">Search</span>
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            placeholder="customer or owner"
            class="h-8 w-56 rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          />
        </label>
        <label class="flex items-center gap-2">
          <span class="text-2xs text-fg-muted">Status</span>
          <select
            v-model="statusFilter"
            class="h-8 rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          >
            <option value="any">Any</option>
            <option value="provisioning">provisioning</option>
            <option value="ready">ready</option>
            <option value="destroying">destroying</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <label class="flex items-center gap-2">
          <span class="text-2xs text-fg-muted">Owner</span>
          <select
            v-model="ownerFilter"
            class="h-8 rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          >
            <option value="anyone">Anyone</option>
            <option value="self">Me</option>
            <option v-for="o in owners" :key="o" :value="o">{{ o }}</option>
          </select>
        </label>
        <span class="ml-auto text-2xs text-fg-muted">Press <span class="mono">/</span> to search · <span class="mono">n</span> for new</span>
      </div>

      <Table
      :columns="columns"
      :rows="filtered"
      :sort-key="sortKey"
      :sort-dir="sortDir"
      :row-key="rowKey"
      :loading="loading"
      empty="No matches for the current filters."
      :on-row-click="open"
      @sort="onSort"
    >
      <template #cell-status="{ row }: { row: Playground }">
        <StatusPill :status="row.status" />
      </template>
      <template #cell-customer="{ row }: { row: Playground }">
        <span class="mono">{{ row.customer }}</span>
      </template>
      <template #cell-owner="{ row }: { row: Playground }">
        <span class="mono text-fg-muted">{{ row.createdBy.upn }}</span>
      </template>
      <template #cell-age="{ row }: { row: Playground }">
        <span class="text-fg-muted">{{ formatAge(row.createdAt) }}</span>
      </template>
      <template #cell-url="{ row }: { row: Playground }">
        <template v-if="row.status === 'ready'">
          <a
            :href="`https://${row.fqdn}`"
            target="_blank"
            rel="noreferrer noopener"
            class="mono text-accent hover:underline dark:text-accent-dark"
            @click.stop
          >{{ row.fqdn }}</a>
          <button
            type="button"
            class="ml-2 text-fg-muted hover:text-fg dark:hover:text-fg-dark"
            @click.stop="copyUrl(row)"
            aria-label="Copy URL"
          ><Copy :size="12" /></button>
        </template>
        <span v-else class="text-fg-muted">—</span>
      </template>
      <template #cell-actions="{ row }: { row: Playground }">
        <button
          type="button"
          class="rounded p-1 text-fg-muted hover:bg-surface dark:hover:bg-bg-dark"
          aria-haspopup="menu"
          data-row-menu-trigger
          @click="toggleMenu(row.customer, $event)"
        ><ChevronDown :size="14" /></button>
        <!--
          The menu lives in <body> via Teleport so it isn't clipped by
          Table.vue's `overflow-x-auto` wrapper (which creates a scroll
          containing block that would otherwise crop absolute children
          and introduce a scrollbar). Positioned with fixed coords
          captured from the trigger's bounding rect at open time.
        -->
        <Teleport to="body">
          <div
            v-if="openMenuFor === row.customer && menuPos"
            class="fixed z-50 w-44 rounded border border-border bg-bg p-1 text-2xs shadow dark:border-border-dark dark:bg-surface-dark"
            role="menu"
            data-row-menu
            :style="{ top: menuPos.top + 'px', right: menuPos.right + 'px' }"
          >
            <button
              type="button"
              class="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left hover:bg-surface dark:hover:bg-bg-dark disabled:opacity-50"
              :disabled="row.status !== 'ready'"
              @click="copyUrl(row); closeMenu()"
            ><Copy :size="12" /> Copy URL</button>
            <a
              v-if="row.status === 'ready'"
              :href="`https://${row.fqdn}`"
              target="_blank"
              rel="noreferrer noopener"
              class="flex items-center gap-1.5 rounded px-2 py-1 hover:bg-surface dark:hover:bg-bg-dark"
              @click="closeMenu()"
            ><ExternalLink :size="12" /> Open URL</a>
            <button
              type="button"
              class="block w-full rounded px-2 py-1 text-left text-status-failed hover:bg-surface dark:hover:bg-bg-dark disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!canDelete(row)"
              :title="!canDelete(row) ? `Only the creator (${row.createdBy.upn}) or an admin can delete this.` : undefined"
              @click="open(row); closeMenu()"
            >Delete</button>
          </div>
        </Teleport>
      </template>
    </Table>
    </template>
  </section>
</template>
